import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { DateTimeInput } from "@/lib/Components/Form/Inputs/DateTimeInput";
import { toDateTimeString } from "@/lib/Formatters/toDateTimeString";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { useFormikContext } from "formik";
import dayjs from "dayjs";
import { z } from "zod";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { logCallbackLeadOutcomeMutation } from "@/app/CallbackLeads/GraphQL/callbackLeadMutations";
import {
  CallbackLeadOutcome,
  LogCallbackLeadOutcomeInput,
} from "@/gql/graphql";
import { CallbackLeadListItem } from "@/app/CallbackLeads/GraphQL/callbackLeadListQuery";
import { CallbackLeadSummary } from "@/app/CallbackLeads/Components/CallbackLeadSummary";
import { Button } from "@/components/catalyst/button";
import { Strong, Text } from "@/components/catalyst/text";
import { ArrowPathIcon, CreditCardIcon } from "@heroicons/react/16/solid";
import { useTranslations } from "use-intl";

/**
 * Outcomes a rep can pick. The rest are set by the system.
 */
const SELECTABLE_OUTCOMES = [
  CallbackLeadOutcome.Booked,
  CallbackLeadOutcome.WillRebook,
  CallbackLeadOutcome.CallBackLater,
  CallbackLeadOutcome.NoAnswer,
  CallbackLeadOutcome.NotInterested,
  CallbackLeadOutcome.WrongNumber,
  CallbackLeadOutcome.DoNotCall,
];

/**
 * Outcomes that leave the lead open, so the rep can set the next call from here
 * instead of opening the snooze dialog.
 */
const SNOOZING_OUTCOMES: CallbackLeadOutcome[] = [
  CallbackLeadOutcome.WillRebook,
  CallbackLeadOutcome.CallBackLater,
  CallbackLeadOutcome.NoAnswer,
];

const QUICK_PICKS = [
  { key: "oneHour", getDate: () => dayjs().add(1, "hour") },
  { key: "threeHours", getDate: () => dayjs().add(3, "hour") },
  {
    key: "tomorrow",
    getDate: () => dayjs().add(1, "day").hour(9).minute(0).second(0),
  },
  {
    key: "threeDays",
    getDate: () => dayjs().add(3, "day").hour(9).minute(0).second(0),
  },
  {
    key: "oneWeek",
    getDate: () => dayjs().add(1, "week").hour(9).minute(0).second(0),
  },
] as const;

function SnoozeFields() {
  const t = useTranslations("callbackLead.outcomeDialog");
  const { values, setFieldValue } =
    useFormikContext<LogCallbackLeadOutcomeInput>();

  if (!SNOOZING_OUTCOMES.includes(values.outcome)) {
    return null;
  }

  return (
    <div className="col-span-full space-y-3">
      <DateTimeInput
        name="snoozed_until"
        label={t("snoozedUntil")}
        description={t("snoozedUntilDescription")}
      />
      <div className="flex flex-wrap gap-2">
        {QUICK_PICKS.map((pick) => (
          <Button
            key={pick.key}
            outline
            className="text-xs"
            onClick={() =>
              setFieldValue("snoozed_until", toDateTimeString(pick.getDate()))
            }
          >
            {t(`quickPick.${pick.key}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function CallbackLeadOutcomeDialog({
  lead,
  onTakePayment,
  onChangeRelocation,
}: {
  lead: CallbackLeadListItem;
  /**
   * Opened by the caller so the payment dialog replaces this one. Absent on a
   * reminder, which has no booking to charge for.
   */
  onTakePayment?: () => void;
  /**
   * Opened by the caller so the dialog replaces this one. Present on a supplier
   * cancellation, where the call is about moving them onto the deal that
   * matched.
   */
  onChangeRelocation?: () => void;
}) {
  const { close } = useDialogState();
  const t = useTranslations("callbackLead");
  const { mutateAsync } = useGqlMutation(logCallbackLeadOutcomeMutation);

  const validation = z
    .object({
      outcome: z.enum(CallbackLeadOutcome),
      note: z.string().nullable().optional(),
      snoozed_until: z.string().nullable().optional(),
    })
    .refine(
      (values) =>
        values.outcome !== CallbackLeadOutcome.CallBackLater ||
        !!values.snoozed_until,
      {
        path: ["snoozed_until"],
        error: t("outcomeDialog.snoozedUntilRequired"),
      },
    );

  const options = [
    {
      label: t("outcomeDialog.outcome"),
      options: SELECTABLE_OUTCOMES.map((outcome) => ({
        label: t(`outcome.${outcome}`),
        value: outcome,
      })),
    },
  ];

  return (
    <BaseDialog title={t("outcomeDialog.title")} className="space-y-6">
      <CallbackLeadSummary leadId={lead.id} />

      {onTakePayment ? (
        <div className="my-6 flex items-center justify-between gap-x-4 rounded-md border border-gray-200 p-4">
          <div>
            <Strong>{t("outcomeDialog.takePaymentTitle")}</Strong>
            <Text className="text-xs">
              {t("outcomeDialog.takePaymentDescription")}
            </Text>
          </div>
          <Button
            color="green"
            onClick={async () => {
              await close();
              onTakePayment();
            }}
          >
            <CreditCardIcon />
            {t("actions.takePayment")}
          </Button>
        </div>
      ) : null}

      {onChangeRelocation ? (
        <div className="my-6 flex items-center justify-between gap-x-4 rounded-md border border-gray-200 p-4">
          <div>
            <Strong>{t("outcomeDialog.changeRelocationTitle")}</Strong>
            <Text className="text-xs">
              {t("outcomeDialog.changeRelocationDescription")}
            </Text>
          </div>
          <Button
            color="green"
            onClick={async () => {
              await close();
              onChangeRelocation();
            }}
          >
            <ArrowPathIcon />
            {t("actions.changeRelocation")}
          </Button>
        </div>
      ) : null}

      <GenericForm<LogCallbackLeadOutcomeInput>
        validationSchema={toFormikValidationSchema(validation)}
        initialValues={{
          outcome: CallbackLeadOutcome.NoAnswer,
          note: lead.note ?? "",
          snoozed_until: null,
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: lead.id,
            input: {
              ...values,
              snoozed_until: SNOOZING_OUTCOMES.includes(values.outcome)
                ? values.snoozed_until
                : null,
            },
          });
          await close();
        }}
      >
        <SelectInput
          name="outcome"
          label={t("outcomeDialog.outcome")}
          options={options}
          className="col-span-full"
        />
        <SnoozeFields />
        <TextAreaInput
          name="note"
          label={t("outcomeDialog.note")}
          className="col-span-full"
          // The dialog grabs focus on open, so Headless has to be told where it goes.
          textAreaProps={{ "data-autofocus": true } as object}
        />
        <DialogSubmitButton text={t("outcomeDialog.submit")} />
      </GenericForm>
    </BaseDialog>
  );
}
